import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export interface User {
  id: string;
  username: string;
  email?: string;
  isAdmin?: boolean;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (username: string, password: string) => Promise<void>;
  register: (username: string, password: string, email?: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  isLoading: boolean;
  getAccessToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const API_URL = typeof window !== 'undefined' && window.location.hostname === 'localhost'
  ? 'http://localhost:3002'
  : 'https://tournaments-backend.fawesome-hypercasual.workers.dev';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load token from localStorage on mount
  useEffect(() => {
    console.log('[AuthContext] Loading auth data from localStorage...');
    const storedToken = localStorage.getItem('auth_token');
    const storedUser = localStorage.getItem('auth_user');
    
    console.log('[AuthContext] Stored data:', {
      hasToken: !!storedToken,
      tokenLength: storedToken?.length || 0,
      hasUser: !!storedUser,
    });
    
    if (storedToken && storedUser) {
      try {
        setToken(storedToken);
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
        console.log('[AuthContext] Auth data loaded:', {
          tokenLength: storedToken.length,
          user: parsedUser,
        });
        // Verify token is still valid
        verifyToken(storedToken).catch((error) => {
          console.error('[AuthContext] Token verification failed on load:', error);
        });
      } catch (error) {
        console.error('[AuthContext] Failed to load auth data:', error);
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_user');
      }
    } else {
      console.log('[AuthContext] No stored auth data found');
    }
    setIsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verifyToken = async (tokenToVerify: string) => {
    try {
      console.log('[Auth] Verifying token...');
      const response = await fetch(`${API_URL}/auth/verify`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${tokenToVerify}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        console.error('[Auth] Token verification failed:', response.status, response.statusText);
        throw new Error('Token invalid');
      }

      const data = await response.json();
      console.log('[Auth] Token verification response:', {
        hasToken: !!data.token,
        tokenChanged: data.token && data.token !== tokenToVerify,
        user: data.user,
        userIsAdmin: data.user?.isAdmin,
        userIsAdminType: typeof data.user?.isAdmin,
      });
      
      // Update token if a new one was returned (e.g., admin status changed)
      if (data.token && data.token !== tokenToVerify) {
        console.log('[Auth] Token refreshed - admin status may have changed');
        setToken(data.token);
        localStorage.setItem('auth_token', data.token);
      }
      
      setUser(data.user);
      localStorage.setItem('auth_user', JSON.stringify(data.user));
      console.log('[Auth] User state updated:', {
        id: data.user?.id,
        username: data.user?.username,
        email: data.user?.email,
        isAdmin: data.user?.isAdmin,
      });
    } catch (error) {
      console.error('[Auth] Token verification failed:', error);
      logout();
    }
  };

  const login = async (username: string, password: string) => {
    try {
      console.log('[Auth] Attempting login for:', username);
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      if (!response.ok) {
        const error = await response.json();
        console.error('[Auth] Login failed:', error);
        throw new Error(error.error || 'Login failed');
      }

      const data = await response.json();
      const { token: newToken, user: userData } = data;

      console.log('[Auth] Login successful:', {
        username: userData.username,
        email: userData.email,
        isAdmin: userData.isAdmin,
        isAdminType: typeof userData.isAdmin,
        hasToken: !!newToken,
      });

      setToken(newToken);
      setUser(userData);
      localStorage.setItem('auth_token', newToken);
      localStorage.setItem('auth_user', JSON.stringify(userData));
    } catch (error: any) {
      console.error('[Auth] Login error:', error);
      throw error;
    }
  };

  const register = async (username: string, password: string, email?: string) => {
    try {
      const response = await fetch(`${API_URL}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password, email }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Registration failed');
      }

      const data = await response.json();
      const { token: newToken, user: userData } = data;

      setToken(newToken);
      setUser(userData);
      localStorage.setItem('auth_token', newToken);
      localStorage.setItem('auth_user', JSON.stringify(userData));
    } catch (error: any) {
      console.error('Registration error:', error);
      throw error;
    }
  };

  const logout = () => {
    console.log('[AuthContext] Logging out - clearing token and user');
    setToken(null);
    setUser(null);
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
  };

  const getAccessToken = (): Promise<string | null> => {
    console.log('[AuthContext] getAccessToken called:', {
      hasToken: !!token,
      tokenLength: token?.length || 0,
      tokenPrefix: token ? token.substring(0, 20) + '...' : 'null',
    });
    return Promise.resolve(token);
  };

  const value: AuthContextType = {
    user,
    token,
    login,
    register,
    logout,
    isAuthenticated: !!user && !!token,
    isLoading,
    getAccessToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
